import React, { useRef } from 'react';
import { Drawer, Form, Input, Select, Button, message, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import './CreateTemplateDrawer.css';
import { templateService } from '../../../../api';
import { TEMPLATE_CATEGORIES } from '../..';
import {
  DEFAULT_COMPRESS_THRESHOLD_BYTES,
  uploadImageWithOptionalCompression,
} from '../../../../utils/imageUpload';
import CompressionConfirmModal from '../../../ImageCompressionModal/CompressionConfirmModal';

export default function CreateTemplateDrawer({ open, onClose, onCreate }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [fileList, setFileList] = React.useState([]);
  const [thumbnailUrl, setThumbnailUrl] = React.useState('');
  const [isUploading, setIsUploading] = React.useState(false);
  const [compressModalOpen, setCompressModalOpen] = React.useState(false);
  const [compressFileSize, setCompressFileSize] = React.useState(0);
  const compressChoiceRef = useRef(null);
  const categories = TEMPLATE_CATEGORIES;

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      setThumbnailUrl('');
      setFileList([]);
    }
  }, [open, form]);

  const closeCompressModal = (choice) => {
    setCompressModalOpen(false);
    const pending = compressChoiceRef.current;
    compressChoiceRef.current = null;
    if (!pending) return;

    if (choice === 'cancel') {
      pending.reject(Object.assign(new Error('cancelled'), { isCancel: true }));
      return;
    }

    message.loading({
      content:
        choice === 'compress'
          ? 'Compressing and uploading image...'
          : 'Uploading original image...',
      key: 'ctd-upload',
    });
    pending.resolve(choice === 'compress');
  };

  const handleBeforeUpload = async (file) => {
    try {
      setIsUploading(true);

      if (file.size <= DEFAULT_COMPRESS_THRESHOLD_BYTES) {
        message.loading({ content: 'Uploading image...', key: 'ctd-upload' });
      }

      const url = await uploadImageWithOptionalCompression(file, {
        thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
        promptCompress: async (largeFile) => {
          message.destroy('ctd-upload');
          return new Promise((resolve, reject) => {
            setCompressFileSize(largeFile.size);
            compressChoiceRef.current = { resolve, reject };
            setCompressModalOpen(true);
          });
        },
      });

      setThumbnailUrl(url);
      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url,
          thumbUrl: url,
        },
      ]);
      message.success({ content: 'Image uploaded', key: 'ctd-upload' });
    } catch (error) {
      if (error?.isCancel) {
        return false;
      }
      console.error('Failed to upload thumbnail:', error);
      message.error({ content: error?.message || 'Failed to upload image', key: 'ctd-upload' });
      setThumbnailUrl('');
      setFileList([]);
    } finally {
      setIsUploading(false);
    }

    return false;
  };

  /**
   * Only react to the user removing the file. Do not mirror Ant Design's in-flight fileList
   * (it adds object-URL previews before Cloudinary finishes), or a failed upload still looks "uploaded".
   */
  const handleUploadChange = ({ fileList: newFileList }) => {
    if (newFileList.length === 0) {
      setThumbnailUrl('');
      setFileList([]);
    }
  };

  const beforeUpload = (file) => handleBeforeUpload(file);

  const tagRender = ({ label, closable, onClose }) => {
    const display = typeof label === 'string' && label.length > 10 ? label.slice(0, 10) + '…' : label;
    return (
      <span className="ctd-select-tag">
        {display}
        {closable && (
          <span className="ctd-select-tag-close" onClick={onClose}>×</span>
        )}
      </span>
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = {
        name: values.name.trim(),
        milepadData: '{}',
        isActive: values.isActive === 'true' || values.isActive === true,
        sort: 0,
        thumbnail: thumbnailUrl || 'string',
        docType: 'mileideation',
        category: values.categories || []
      };
      const res = await templateService.createTemplate(payload);
      const createdTemplate = res?.data?.data;
      const templateId = createdTemplate?._id;

      message.success('Template created successfully');
      onCreate?.();
      onClose();

      if (templateId) {
        window.open(`/template/${templateId}`, '_blank');
      }
    } catch (error) {
      if (error?.errorFields) return;
      const msg = error?.response?.data?.message || 'Failed to create template';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const FooterBar = (
    <div className="ctd-bar">
      <Button
        type="primary"
        size="large"
        block
        className="ctd-create-btn"
        loading={loading}
        disabled={loading || isUploading}
        onClick={handleSubmit}
      >
        Create Template
      </Button>
    </div>
  );

  return (
    <>
      <Drawer
        className="ctd-drawer"
        title={<span style={{ fontWeight: 600, color: '#526bb1' }}>Create Template</span>}
        visible={open}
        onClose={onClose}
        width={640}
        destroyOnClose={false}
        styles={{ body: { background: '#f5f6f7', padding: 16 } }}
      >
        <div className="ctd" style={{ background: '#fff', borderRadius: 8, padding: '20px' }}>
          <Form form={form} layout="vertical" requiredMark={true} initialValues={{ isActive: 'true' }}>
            <Form.Item
              label={<span className="ctd-label">Name</span>}
              name="name"
              rules={[{ required: true, message: 'Enter template name' }]}
            >
              <Input placeholder="Enter template name" />
            </Form.Item>

            <Form.Item
              label={<span className="ctd-label">Active</span>}
              name="isActive"
              rules={[{ required: true, message: 'Select status' }]}
            >
              <Select placeholder="Select status">
                <Select.Option value="true">Active</Select.Option>
                <Select.Option value="false">Inactive</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={<span className="ctd-label">Categories</span>}
              name="categories"
              rules={[{ required: true, message: 'Select categories' }]}
            >
              <Select
                mode="multiple"
                placeholder="Select categories"
                showArrow
                className="ctd-categories-select"
                tagRender={tagRender}
                maxTagCount={5}
                maxTagPlaceholder={selected => `+${selected.length} more`}
              >
                {(categories || []).map(cat => (
                  <Select.Option key={cat} value={cat}>
                    {cat}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={<span className="ctd-label">Thumbnail</span>}>
              <Upload
                accept="image/*"
                listType="picture"
                fileList={fileList}
                beforeUpload={beforeUpload}
                onChange={handleUploadChange}
                maxCount={1}
                disabled={isUploading}
              >
                {fileList.length === 0 && (
                  <div className="ctd-upload-placeholder">
                    <PlusOutlined style={{ marginRight: 6, fontSize: 11 }} />
                    {isUploading ? 'Uploading…' : 'Upload thumbnail image'}
                  </div>
                )}
              </Upload>
            </Form.Item>
          </Form>

          <div style={{ paddingTop: 8 }}>
            {FooterBar}
          </div>
        </div>
      </Drawer>
      <CompressionConfirmModal
        open={compressModalOpen}
        fileSize={compressFileSize}
        onCompress={() => closeCompressModal('compress')}
        onUploadOriginal={() => closeCompressModal('original')}
        onCancel={() => closeCompressModal('cancel')}
        zIndex={3000}
        getContainer={() => document.body}
      />
    </>
  );
}
